import posthog from 'posthog-js';

/**
 * PostHog, wrapped so callers never have to care whether it is configured.
 *
 * The key comes from the per-environment `window.ReactAppEnv` block in
 * environments/*.json, the same channel Clarity and Sentry use. One `vite
 * build` serves every environment, so a build-time VITE_ variable could not
 * hold a different key for dev and production; this can. Without a key nothing
 * is initialized and every call below is a no-op, which is what local dev, CI,
 * and preview get.
 */

let initialized = false;

export function initAnalytics() {
  const key = window.ReactAppEnv?.posthog?.key;
  if (!key) {
    return;
  }

  posthog.init(key, {
    api_host: window.ReactAppEnv?.posthog?.host || 'https://us.i.posthog.com',
    // We send the events we mean to send, and nothing else: no click/input
    // autocapture, no automatic pageviews, no session recordings, and no
    // person profile until someone logs in and we identify them by user id.
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    disable_session_recording: true,
    person_profiles: 'identified_only',
    session_recording: { maskAllInputs: true },
  });
  initialized = true;
}

/** Sends an event. Never throws, never blocks the flow it instruments. */
export function track(event: string, properties?: Record<string, unknown>) {
  if (!initialized) {
    return;
  }
  posthog.capture(event, properties);
}

/** Ties events to a logged-in user. User id only: no email, no name. */
export function identify(userId: string) {
  if (!initialized) {
    return;
  }
  posthog.identify(userId);
}

/**
 * Forgets who the user was, at logout. Without this the next person on a
 * shared browser keeps sending events under the previous user's identity.
 */
export function resetIdentity() {
  if (!initialized) {
    return;
  }
  posthog.reset();
}

/** The content properties every share event carries. */
export interface ShareContent {
  content_type: 'topic';
  course_id: string;
  concept_id: string;
  topic_id: string;
}

const SHARE_REF_KEY = 'sgapp_share_ref';
/** How long a share stays credited for a signup or purchase. */
const SHARE_REF_TTL = 30 * 24 * 60 * 60 * 1000;

/**
 * Remembers that this visitor arrived from a shared lesson, so a signup or
 * purchase made later in the same browser can say which share earned it.
 * Stored rather than passed along because the visitor leaves for the
 * marketing site in between.
 */
export function rememberShareRef(ref: {
  share_topic_id: string;
  share_channel: string;
}) {
  try {
    localStorage.setItem(
      SHARE_REF_KEY,
      JSON.stringify({ ...ref, ts: Date.now() }),
    );
  } catch {
    // Private browsing or a full quota. Attribution is nice to have.
  }
}

/** Share attribution to spread into a later event, or nothing. */
export function shareAttribution(): Record<string, string> {
  try {
    const { ts, ...ref } = JSON.parse(
      localStorage.getItem(SHARE_REF_KEY) || '{}',
    );
    return Date.now() - ts < SHARE_REF_TTL ? ref : {};
  } catch {
    return {};
  }
}
